import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";

import TopNav from "../components/navigation/TopNav";
import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Card from "../components/ui/Card";
import KeyValueList from "../components/ui/KeyValueList";

const shellStyle = {
  width: "100%",
  maxWidth: "760px",
  margin: "0 auto",
};

const errorTextStyle = {
  color: "var(--color-danger)",
};

const bodyTextStyle = {
  margin: 0,
  color: "var(--color-text)",
};

const mutedTextStyle = {
  margin: 0,
  color: "var(--color-text-muted)",
};

const titleTextStyle = {
  margin: 0,
  fontSize: "var(--font-size-xl)",
  fontWeight: "var(--font-weight-bold)",
  color: "var(--color-text)",
};

const ExcursionSummary = () => {
  const { t } = useTranslation();
  const { user, loadingUser } = useAuth();

  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setError(null);

        const excursionId = user?.excursion?.excursion_id;
        if (!excursionId) {
          setSummary(null);
          return;
        }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/excursions/${excursionId}/summary`,
          { headers }
        );

        setSummary(res.data);
      } catch (err) {
        console.error(err);
        setError(t("excursion_summary.errors.load_failed"));
      }
    };

    if (user && token) {
      fetchSummary();
    }
  }, [user, token, headers, t]);

  if (loadingUser) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("excursion_summary.title")} mode="main" />}
        isLoading
      />
    );
  }

  if (!user) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("excursion_summary.title")} mode="main" />}
        isError
        errorState={
          <div style={errorTextStyle}>
            {t("excursion_summary.errors.no_user")}
          </div>
        }
      />
    );
  }

  if (error) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("excursion_summary.title")} mode="main" />}
        isError
        errorState={<div style={errorTextStyle}>{error}</div>}
      />
    );
  }

  const isAdmin = user.role === "admin";
  const subscriptionStatus = String(user.subscription_status || "");

  if (!isAdmin && subscriptionStatus === "pending") {
    return (
      <StandardScreen
        topNav={<TopNav title={t("excursion_summary.title")} mode="main" />}
      >
        <ScreenContent>
          <div style={shellStyle}>
            <Card>
              <Stack gap="4">
                <h2 style={titleTextStyle}>
                  {t("excursion_summary.pending.greeting", {
                    name: user.name,
                    surname: user.surname,
                  })}
                </h2>

                <p style={bodyTextStyle}>
                  {t("excursion_summary.pending.welcome", {
                    excursion: user.excursion?.short_name,
                  })}
                </p>

                <p style={mutedTextStyle}>
                  {t("excursion_summary.pending.waiting")}
                </p>
              </Stack>
            </Card>
          </div>
        </ScreenContent>
      </StandardScreen>
    );
  }

  const allowedStatuses = ["approved", "suspended"];
  const allowedMember = allowedStatuses.includes(subscriptionStatus);

  if (!isAdmin && !allowedMember) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("excursion_summary.title")} mode="main" />}
      >
        <ScreenContent>
          <div style={shellStyle}>
            <Card variant="accent">
              <strong>{t("excursion_summary.errors.no_access")}</strong>
            </Card>
          </div>
        </ScreenContent>
      </StandardScreen>
    );
  }

  return (
    <StandardScreen
      topNav={<TopNav title={t("excursion_summary.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={shellStyle}>
          {summary ? (
            <Stack gap="5">
              <Card
                title={t("excursion_summary.overview.title")}
                subtitle={t("excursion_summary.overview.subtitle")}
              >
                <KeyValueList
                  items={[
                    {
                      label: t("excursion_summary.fields.id"),
                      value: summary.excursion_id,
                    },
                    {
                      label: t("excursion_summary.fields.name"),
                      value: summary.short_name || "—",
                    },
                    {
                      label: t("excursion_summary.fields.about"),
                      value: summary.definition || "—",
                    },
                    {
                      label: t("excursion_summary.fields.start"),
                      value: summary.start_date || "—",
                    },
                    {
                      label: t("excursion_summary.fields.end"),
                      value: summary.end_date || "—",
                    },
                    {
                      label: t("excursion_summary.fields.currency"),
                      value: summary.excursion_currency || "—",
                    },
                    {
                      label: t("excursion_summary.fields.status"),
                      value: summary.status || "—",
                    },
                  ]}
                />
              </Card>

              <Card
                title={t("excursion_summary.financial.title")}
                subtitle={t("excursion_summary.financial.subtitle")}
              >
                <KeyValueList
                  items={[
                    {
                      label: t("excursion_summary.fields.bill_count"),
                      value: summary.number_of_bills ?? 0,
                    },
                    {
                      label: t("excursion_summary.fields.total_expense"),
                      value: summary.total_value_of_bills ?? 0,
                    },
                    {
                      label: t("excursion_summary.fields.members"),
                      value: summary.number_of_subscribed_members ?? 0,
                    },
                  ]}
                />
              </Card>
            </Stack>
          ) : (
            <Card>{t("excursion_summary.loading")}</Card>
          )}
        </div>
      </ScreenContent>
    </StandardScreen>
  );
};

export default ExcursionSummary;